import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
} from "@angular/core";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { WorkflowDisplayPdfFrameService } from "../../../core/services/workflow-display-pdf-frame.service";
import { PreSalesProjectWorkflowList } from "../../../core/models/orders/preSalesProjectWorkflowList";
import { ProjectFileDto } from "../../../core/models/project-workflow/projectFileDto";
import { WorkflowEmailData } from "../../../core/models/project-workflow/workflowEmailData";
import { EnumWorkflowEmailState } from "../../../shared/enums/workflowEmailState";
import { SelectModule } from "primeng/select";
import { CommonModule } from "@angular/common";
import { EmailTemplates } from "../../../core/models/orders/emailTemplates";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";

@Component({
  selector: "jni-workflow-email-header",
  templateUrl: "./workflow-email-header.component.html",
  styleUrls: ["./workflow-email-header.component.scss"],
  imports: [
    ReactiveFormsModule,
    SelectModule,
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    InputTextModule,
  ],
})
export class WorkflowEmailHeaderComponent implements OnInit, OnChanges {
  @Input() displayRushRevisedSection = true;
  @Input() parent!: FormGroup;
  @Input() templates: EmailTemplates[] = [];
  @Input() poNumber: string[] = [];
  @Input() formErrors!: Record<string, string>;
  @Input() validationMessages!: Record<string, Record<string, string>>;
  @Input() emailButtonStatus = false;
  @Input() poNumberRevision: string[] = [];
  @Input() workflowEmailData: WorkflowEmailData = {} as WorkflowEmailData;
  @Input() projectHeaderList: PreSalesProjectWorkflowList[] = [];
  @Input() projectFilesList: ProjectFileDto[] = [];
  @Output() fetchVendorInformation = new EventEmitter();
  @Output() templateSelectedEvent = new EventEmitter();
  @Output() logValidationErrorsEvent = new EventEmitter();
  @Output() sendEmailEvent = new EventEmitter();
  @Output() fetchWatermarkedPdf = new EventEmitter<{
    rush: boolean;
    revised: boolean;
  }>();
  @Output() getProjectFilesEvent = new EventEmitter<number>();
  @Output() sendSelectedProjectFileEvent = new EventEmitter<ProjectFileDto>();

  base64 = "";
  rush = "Rush";
  revised = "Revised";
  rushRevised = "Rush/Revised";
  rushCheck = false;
  revisedCheck = false;
  additionalSubject = "";
  displayWorkflowSection = true;
  displaySaleOrderSection = false;
  windowState = 0;
  saleOrderId = "";
  workflowId = 0;
  projectIdForFiles = "";
  projectFile = {} as ProjectFileDto | null;

  constructor(
    private displayPdfFrameService: WorkflowDisplayPdfFrameService,
    private _activatedRoute: ActivatedRoute
  ) {
    this._activatedRoute.params.subscribe(params => {
      this.windowState = params["windowState"] ?? 0;
      this.saleOrderId = params["id"];
    });
  }
  ngOnInit(): void {
    this.onTemplate();

    if (this.windowState == EnumWorkflowEmailState.PrintWorkflowSummary) {
      this.displayWorkflowSection = false;
      this.displaySaleOrderSection = true;
    }
  }

  ngOnChanges(): void {
    if (this.workflowEmailData?.windowState) {
      this.saleOrderId = this.workflowEmailData.saleOrderId;
      this.windowState = this.workflowEmailData.windowState;
      this.workflowId = this.workflowEmailData.workflowId;
      this.patchValueInSubject("", "");
    }
  }

  onTemplate() {
    this.displayPdfFrameService.selectedtemplates$.subscribe(() => {
      this.onTemplateSelection();
    });
  }

  logValidationErrors() {
    this.logValidationErrorsEvent.emit();
  }

  rushRevisedFieldEnable() {
    this.parent.get(["header", "additionalSubject"])?.enable();
  }

  rushRevisedFieldDisabled() {
    this.parent.get(["header", "additionalSubject"])?.disable();
  }

  clearRushRevisedField() {
    this.parent.patchValue({
      header: {
        additionalSubject: "",
      },
    });
    this.rushCheck = false;
    this.revisedCheck = false;
    this.additionalSubject = "";
  }

  onTemplateSelection() {
    this.templateSelectedEvent.emit(
      this.parent.get(["header", "templateId"])?.value || 0
    );
  }

  addPdfWaterMark() {
    this.fetchWatermarkedPdf.emit({
      rush: this.rushCheck,
      revised: this.revisedCheck,
    });
  }

  patchValueInSubject(value: string, additionalSubject: string) {
    if (this.windowState == EnumWorkflowEmailState.PrintWorkflowSummary) {
      this.parent.patchValue({
        header: {
          subject: `Nadel Proposal - ${
            this.parent.get(["header", "workflowId"])?.value || ""
          }  ${value}`,
        },
      });
    } else if (value && !additionalSubject) {
      this.parent.patchValue({
        header: {
          subject: `Nadel Proposal - ${
            this.parent.get(["header", "workflowId"])?.value || ""
          } - ${value}`,
        },
      });
    } else if (value && additionalSubject) {
      this.parent.patchValue({
        header: {
          subject: `Nadel PO - ${
            this.parent.get(["header", "workflowId"])?.value || ""
          } - ${value} - ${additionalSubject}`,
        },
      });
    } else if (!value && additionalSubject) {
      this.parent.patchValue({
        header: {
          subject: `Nadel PO - ${
            this.parent.get(["header", "workflowId"])?.value || ""
          } - ${additionalSubject}`,
        },
      });
    } else {
      this.parent.patchValue({
        header: {
          subject: `Nadel PO - ${
            this.parent.get(["header", "workflowId"])?.value || ""
          }`,
        },
      });
    }
  }

  onAdditionalSubject(event: Event) {
    const target = event.target as HTMLInputElement;
    this.additionalSubject = target.value;
    if (this.additionalSubject) {
      if (this.rushCheck && this.revisedCheck) {
        this.patchValueInSubject(this.rushRevised, this.additionalSubject);
      } else if (
        this.windowState == EnumWorkflowEmailState.PrintWorkflowSummary
      ) {
        this.parent.patchValue({
          header: {
            subject: `Nadel Proposed - ${
              this.parent.get(["header", "workflowId"])?.value || ""
            }  - ${this.additionalSubject}`,
          },
        });
      } else {
        if (this.rushCheck && !this.revisedCheck) {
          this.patchValueInSubject(this.rush, this.additionalSubject);
        } else if (!this.rushCheck && this.revisedCheck) {
          this.patchValueInSubject(this.revised, this.additionalSubject);
        } else if (!this.rushCheck && !this.revisedCheck) {
          this.patchValueInSubject("", this.additionalSubject);
        }
      }
    } else {
      this.additionalSubject = "";
      if (this.rushCheck && this.revisedCheck) {
        this.patchValueInSubject(this.rushRevised, this.additionalSubject);
      } else {
        if (this.rushCheck && !this.revisedCheck) {
          this.patchValueInSubject(this.rush, this.additionalSubject);
        } else if (!this.rushCheck && this.revisedCheck) {
          this.patchValueInSubject(this.revised, this.additionalSubject);
        } else if (!this.rushCheck && !this.revisedCheck) {
          this.patchValueInSubject("", this.additionalSubject);
        }
      }
    }
  }

  onEmailPO() {
    this.sendEmailEvent.emit();
  }

  onProjectSelectionChangesForFiles() {
    this.projectFile = null;
    this.getProjectFilesEvent.emit(Number(this.projectIdForFiles));
  }

  onAddProjectFileAttachment() {
    if (this.projectFile && this.projectFile !== ({} as ProjectFileDto)) {
      const file = this.projectFile;
      this.projectFile = null;
      this.sendSelectedProjectFileEvent.emit(file);
    }
  }
}
